import gql from "graphql-tag";
import GraphQLClient, { RequestParam } from "../api";
import { Novel } from "../models";
import { AssociatedDataType } from "../models/common";

interface INovelClient {
  fetchAllNovel: () => Promise<Novel[]>;
  fetchNovelByExternalUser: (userId: string, novelId: string) => Promise<Novel>;
  fetchNovelById: (id: string) => Promise<Novel>;

  setAssociatedData: (
    serviceName: "NolaNovel" | "NolaPublishing",
    novelId: string,
    id: string | null
  ) => Promise<{ novelId: string; associatedData: AssociatedDataType }>;

  createNovel: (title: string, genre?: string | null, category?: string[] | null) => Promise<string>;
  updateNovel: (novel: Novel) => Promise<Novel>;
  deleteNovel: (novelId: string, title: string) => Promise<void>;
}

export class NovelClient implements INovelClient {
  async fetchNovelById(id: string): Promise<Novel> {
    const params: RequestParam = {
      query: fetchNovelById,
      variables: {
        novelId: id,
      },
    };

    const { novel } = await GraphQLClient.request(params);
    return novel;
  }

  async fetchAllNovel(): Promise<Novel[]> {
    const { novels } = await GraphQLClient.request({ query: fetchAllNovel });
    return novels.items;
  }

  async fetchNovelByExternalUser(userId: string, novelId: string): Promise<Novel> {
    const params: RequestParam = {
      query: fetchNovelByExternalUser,
      variables: {
        input: {
          userId,
          novelId,
        },
      },
    };

    const { novelByExternalUser } = await GraphQLClient.request(params);
    return novelByExternalUser;
  }

  async setAssociatedData(
    serviceName: "NolaNovel" | "NolaPublishing",
    novelId: string,
    id: string | null
  ): Promise<{ novelId: string; associatedData: AssociatedDataType }> {
    const params: RequestParam = {
      query: setNovelAssociatedData,
      variables: {
        input: {
          serviceName,
          novelId,
          id,
        },
      },
    };

    const { setNovelAssociatedData: result } = await GraphQLClient.request(params);
    return result;
  }

  async createNovel(title: string, genre?: string | null, category?: string[] | null): Promise<string> {
    const params: RequestParam = {
      query: createNovel,
      variables: {
        input: {
          title,
          genre,
          category,
        },
      },
    };

    const { createNovel: result } = await GraphQLClient.request(params);
    const { novelId } = result;
    return novelId;
  }

  async updateNovel(novel: Novel): Promise<Novel> {
    const input = {
      ...novel,
    };
    delete input.associatedData;
    delete input.updatedAtLatestDataInNovel;
    delete input.createdAt;
    delete input.updatedAt;
    const params: RequestParam = {
      query: updateNovel,
      variables: {
        input,
      },
    };

    const { updateNovel: result } = await GraphQLClient.request(params);
    return result;
  }

  async deleteNovel(novelId: string, title: string): Promise<void> {
    const params: RequestParam = {
      query: deleteNovel,
      variables: {
        input: {
          novelId,
          title,
        },
      },
    };
    await GraphQLClient.request(params);
  }
}

/**
 * Query
 */

const fetchAllNovel = gql`
  query Novels {
    novels {
      items {
        novelId
        title
        image
        description
        genre
        category
        workStatus
        pinned
        associatedData {
          nolaNovel {
            id
          }
        }
        updatedAtLatestDataInNovel
        createdAt
        updatedAt
      }
    }
  }
`;

const fetchNovelByExternalUser = gql`
  query NovelByExternalUser($input: GetNovelByExternalUserInput!) {
    novelByExternalUser(input: $input) {
      novelId
      title
      image
      description
      createdAt
      updatedAt
    }
  }
`;

const fetchNovelById = gql`
  query Novel($novelId: ID!) {
    novel(novelId: $novelId) {
      novelId
      title
      image
      description
      genre
      category
      workStatus
      associatedData {
        nolaNovel {
          id
        }
      }
      updatedAtLatestDataInNovel
      createdAt
      updatedAt
    }
  }
`;

const setNovelAssociatedData = gql`
  mutation SetNovelAssociatedData($input: SetNovelAssociatedDataInput!) {
    setNovelAssociatedData(input: $input) {
      novelId
      associatedData {
        nolaNovel {
          id
        }
      }
    }
  }
`;

const createNovel = gql`
  mutation CreateNovel($input: CreateNovelInput!) {
    createNovel(input: $input) {
      novelId
    }
  }
`;

const updateNovel = gql`
  mutation UpdateNovel($input: UpdateNovelInput!) {
    updateNovel(input: $input) {
      description
      novelId
      title
      image
      genre
      category
      workStatus
      pinned
      associatedData {
        nolaNovel {
          id
        }
      }
      updatedAtLatestDataInNovel
      createdAt
      updatedAt
    }
  }
`;

const deleteNovel = gql`
  mutation DeleteNovel($input: DeleteNovelInput!) {
    deleteNovel(input: $input)
  }
`;
